<script lang="ts">
  import { storyblokEditable } from '@storyblok/svelte';
  import { TrustPilotProductReviewList } from '@stickerapp-org/athena/TrustPilotProductReviewList';
  import { PageSection } from '@athena-lib/Page';
  import { HeadingSmall } from '@athena-lib/Text';
  import { Stack, StackItem } from '@athena-lib/Stack';
  import { Tab, TabPanel, Tabs } from '@athena-lib/Tabs';
  import { SpecsTable } from '@athena-lib/SpecsTable';
  import type { ButtonColor } from '@athena-lib/Button';
  import clsx from 'clsx';
  import type { Richtext, TabsPageSectionBlock } from '@storyblok-types';
  import { getCommonTranslations } from '@athena-lib/commonTranslationsContext';
  import { RichText } from '../RichText';
  import { tabPageSectionClass } from './shared/page-section-utils';
  import { fetchTrustpilotReviews } from './shared/trustpilot-reviews';
  import type { WithSpecs } from '$lib/server/expanders/specsTable';

  import type { WithTrustPilotProductReviews } from '$lib/server/expanders/trustPilotReviewList';
  const { blok }: { blok: WithTrustPilotProductReviews<WithSpecs<TabsPageSectionBlock>> } =
    $props();

  const tabOrdering = $derived.by(() => {
    const order = ['reviews', 'specs'] as const;
    if (!blok.reviewTabFirst) {
      return order.toReversed();
    }
    return order;
  });
  const translations = getCommonTranslations();
</script>

{#snippet specValueSnippet(richText: unknown)}
  <RichText text={richText as Richtext} />
{/snippet}

<PageSection
  id={blok.elementId}
  action={storyblokEditable}
  actionArg={blok}
  ySpace={{
    top: blok.topPadding ?? 'medium',
    bottom: blok.bottomPadding ?? 'medium',
  }}
  class={clsx(tabPageSectionClass, 'scroll-mt-16')}
>
  <Tabs>
    {#snippet tabs()}
      {#each tabOrdering as tab}
        {#if tab === 'reviews'}
          <Tab>{translations.reviewsTabName}</Tab>
        {:else if blok.specTags?.length}
          <Tab>{translations.specsTabName}</Tab>
        {/if}
      {/each}
    {/snippet}
    {#snippet panels()}
      {#each tabOrdering as tab}
        {#if tab === 'reviews'}
          <TabPanel>
            <Stack space="gutter" class="mt-1">
              {#if blok.reviewsHeading}
                <StackItem>
                  <HeadingSmall>
                    {blok.reviewsHeading}
                  </HeadingSmall>
                </StackItem>
              {/if}
              {#if blok.apiResponse}
                <StackItem>
                  <TrustPilotProductReviewList
                    sku={blok.sku}
                    apiResponse={blok.apiResponse}
                    loadMoreButtonColor={(blok.loadReviewsButtonColor as ButtonColor) || 'brand'}
                    fetchProductReviews={fetchTrustpilotReviews}
                  />
                </StackItem>
              {/if}
            </Stack>
          </TabPanel>
        {:else if blok.specTags?.length}
          <TabPanel>
            <Stack space="gutter" class="mt-1">
              {#if blok.specificationHeading}
                <StackItem>
                  <HeadingSmall>
                    {blok.specificationHeading}
                  </HeadingSmall>
                </StackItem>
              {/if}
              <StackItem>
                <SpecsTable
                  tags={blok.specTags}
                  specs={blok.specs.map((spec) => ({
                    tags: (spec.tags as string[]) ?? [],
                    label: spec.label ?? '',
                    value: spec.value ?? '',
                  }))}
                  valueSnippet={specValueSnippet}
                />
              </StackItem>
            </Stack>
          </TabPanel>
        {/if}
      {/each}
    {/snippet}
  </Tabs>
</PageSection>
